import { getFeaturedServicesAction } from '~/api/functions'
import { getPopularServicesAction } from '~/api/functions/services/get-popular-services.action'
import { getServiceByIdAction } from '~/api/functions/services/get-service-by-id.action'
import type { ServiceQuery } from '~/api/functions/services/get-services.action'
import { getServicesAction } from '~/api/functions/services/get-services.action'
import type { Service } from '~/api/typings'
/**
 * SSR-safe composable for fetching services.
 *
 * Uses `useAsyncData` + `useFetch` under the hood so data is fetched
 * on the server during SSR and hydrated on the client without re-fetching.
 */

export const useServices = (
  initialQuery: ServiceQuery = {},
  options: {
    enabled?: Ref<boolean> | ComputedRef<boolean>
  } = {}
) => {
  const query = ref<ServiceQuery>({ ...initialQuery })

  const enabled = computed(() => (options.enabled ? options.enabled.value : true))

  const {
    data: servicesResponse,
    pending: loadingServices,
    error: servicesError,
    refresh: refreshServices,
  } = useAsyncData(
    () => `services-${JSON.stringify(query.value)}`,
    () => getServicesAction(query.value),
    {
      watch: [query],
      immediate: enabled.value,
      default: () => ({
        data: [] as Service[],
        pagination: null,
      }),
    }
  )

  const services = computed(() => servicesResponse.value?.data ?? [])

  const pagination = computed(() => servicesResponse.value?.pagination ?? null)

  const setQuery = (newQuery: ServiceQuery) => {
    query.value = { ...newQuery }
  }

  const setFilters = (filters: ServiceQuery['filters']) => {
    query.value = {
      ...query.value,
      filters,
      page: 1,
    }
  }

  const setFilter = <K extends keyof NonNullable<ServiceQuery['filters']>>(
    key: K,
    value: NonNullable<ServiceQuery['filters']>[K]
  ) => {
    query.value = {
      ...query.value,
      filters: {
        ...query.value.filters,
        [key]: value,
      },
      page: 1,
    }
  }

  const setPage = (page: number) => {
    query.value = {
      ...query.value,
      page,
    }
  }

  const setPerPage = (perPage: number) => {
    query.value = {
      ...query.value,
      per_page: perPage,
      page: 1,
    }
  }

  const setSort = (sort: string) => {
    query.value = {
      ...query.value,
      sort,
      page: 1,
    }
  }

  const clearFilters = () => {
    query.value = {
      ...query.value,
      filters: undefined,
      page: 1,
    }
  }

  return {
    services,
    pagination,
    query,

    loadingServices,
    servicesError,
    refreshServices,

    setQuery,
    setFilters,
    setFilter,
    setPage,
    setPerPage,
    setSort,
    clearFilters,
  }
}

// Fetch Popular Services
export function useService(slug: Ref<string> | string) {
  const resolvedId = typeof slug === 'string' ? ref(slug) : slug

  const {
    data: service,
    pending: loadingService,
    error: serviceError,
    refresh: refreshService,
  } = useAsyncData(`service-${resolvedId.value}`, () => getServiceByIdAction(resolvedId.value), {
    watch: [resolvedId],
    transform: (res) => res?.data ?? null,
    default: () => null as Service | null,
  })

  return {
    service,
    loadingService,
    serviceError,
    refreshService,
  }
}

// Fetch Popular Services
export function usePopularServices() {
  const {
    data: popularServices,
    pending: loadingPopularServices,
    error: popularServicesError,
    refresh: refreshPopularServices,
  } = useAsyncData('popular-services', () => getPopularServicesAction(), {
    transform: (res) => res?.data ?? [],
    default: () => [] as Service[],
  })

  return {
    //Popular Services
    popularServices,
    loadingPopularServices,
    popularServicesError,
    refreshPopularServices,
  }
}

export function useFeaturedServices() {
  const {
    data,
    pending: loading,
    error,
    refresh,
  } = useAsyncData('featured-services', () => getFeaturedServicesAction(), {
    transform: (res) => res?.data ?? [],
    default: () => [] as Service[],
  })

  return {
    //Popular Services
    data,
    loading,
    error,
    refresh,
  }
}
